import React, { useRef, useCallback, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * Simple POC Table component using shadcn/ui Table
 * @param {Array} columns - Array of column definitions
 * @param {Array} data - Array of POC data objects
 * @param {Function} onRowClick - Optional callback when row is clicked
 * @param {String} emptyMessage - Message to show when no data
 * @param {String} className - Additional CSS classes for the table wrapper
 * @param {Function} rowClassName - Function or string to determine row className
 * @param {Function} onLoadMore - Callback to load more data when scrolling near bottom
 * @param {Boolean} hasMore - Whether there are more items to load
 * @param {Boolean} loading - Whether data is currently loading
 */
const POCTable = ({
  columns = [],
  data = [],
  onRowClick,
  emptyMessage = "No POCs available",
  className = "",
  rowClassName,
  maxHeight = "500px",
  onLoadMore,
  hasMore = false,
  loading = false,
}) => {
  const scrollContainerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);

  // Handle scroll event for lazy loading
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !onLoadMore || !hasMore || loading || isLoadingMoreRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    
    console.log('Scroll Debug:', {
      scrollTop,
      scrollHeight,
      clientHeight,
      scrollBottom,
      hasMore,
      loading,
      isLoadingMore: isLoadingMoreRef.current
    });

    // Load more when within 100px of the bottom
    if (scrollBottom < 100) {
      console.log('Triggering load more...');
      isLoadingMoreRef.current = true;
      onLoadMore();
      
      // Reset the loading flag after a delay
      setTimeout(() => {
        isLoadingMoreRef.current = false;
      }, 1000);
    }
  }, [onLoadMore, hasMore, loading]);

  // Reset loading ref when loading state changes
  useEffect(() => {
    if (!loading) {
      isLoadingMoreRef.current = false;
    }
  }, [loading]);

  // Attach scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className={`rounded-lg border border-gray-100 overflow-hidden ${className}`}>
      <div 
        ref={scrollContainerRef}
        style={{ maxHeight }} 
        className="overflow-y-auto"
      >
        <Table>
          <TableHeader className="bg-gray-100 sticky top-0 z-10">
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.headerClassName || ""}
                  style={column.width ? { width: column.width } : {}}
                >
                  {typeof column.header === 'function' 
                    ? column.header() 
                    : column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white divide-y divide-gray-200">
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-12 text-gray-500"
                >
                  <div className="flex flex-col items-center">
                    <p className="text-lg font-medium mb-2">{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, rowIndex) => {
                const computedRowClassName = typeof rowClassName === 'function'
                  ? rowClassName(row, rowIndex)
                  : rowClassName || "";

                return (
                  <TableRow
                    key={row.poc_id || row.id || rowIndex}
                    onClick={() => onRowClick && onRowClick(row, rowIndex)}
                    className={computedRowClassName}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={`${row.poc_id || row.id || rowIndex}-${column.key}`}
                        className={column.cellClassName || ""}
                      >
                        {column.render
                          ? column.render(row[column.key], row, rowIndex)
                          : row[column.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {loading && hasMore && (
          <div className="py-4 text-center bg-gray-50 border-t border-gray-100">
            <div className="inline-flex items-center gap-2 text-sm text-gray-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              Loading more POCs...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default POCTable;
